/**
 * Types & hooks de données du module Parc matériel — DÉRIVÉS du routeur tRPC.
 *
 * On infère les shapes de sortie depuis `AppRouter` (`inferRouterOutputs`)
 * plutôt que d'importer les types des use cases (couche application interne de
 * l'API). Point de vérité unique : le contrat réseau. Centralise aussi les
 * query keys (cohérence des invalidations après mutation).
 *
 * Online-first : pas de cache hors-ligne ici (chantier d'infra séparé).
 */

import type { AppRouter } from "@champ-erp/api/router";
import { useQuery } from "@tanstack/react-query";
import type { inferRouterOutputs } from "@trpc/server";

import { resoudreDomaineIdActif } from "../../../auth/tenant-actif.js";
import { useCan } from "../../../auth/use-can.js";
import { useMe } from "../../../auth/use-me.js";
import { tenantQueryKey } from "../../../lib/tenant-query-key.js";
import { trpc } from "../../../lib/trpc.js";

type Outputs = inferRouterOutputs<AppRouter>;

/** Une ligne du parc (tuile de liste). */
export type MaterielListItem = Outputs["parcMateriel"]["listerParc"][number];

/** Fiche détail d'un matériel (+ plans évalués + historique). */
export type MaterielDetail = Outputs["parcMateriel"]["getMateriel"];

/** Un plan d'entretien de la fiche (avec son état d'alerte évalué). */
export type PlanFiche = MaterielDetail["plans"][number];

/** Une entrée d'historique d'entretien (timeline). */
export type EntretienFiche = MaterielDetail["historique"][number];

/** Une alerte matériel (bloc dashboard). */
export type AlerteMateriel = Outputs["parcMateriel"]["listerAlertes"][number];

// ────────────────────────────────────────────────────────────────────────────
// Query keys — un seul endroit pour rester cohérent à l'invalidation
// ────────────────────────────────────────────────────────────────────────────

export const materielQueryKeys = {
  /** Liste du parc (clé inclut le filtre statut). */
  parc: (domaineId: string | null, statut?: string) =>
    tenantQueryKey(domaineId, "parcMateriel.listerParc", { statut: statut ?? "actif" }),
  /** Fiche d'un matériel. */
  fiche: (domaineId: string | null, id: string) =>
    tenantQueryKey(domaineId, "parcMateriel.getMateriel", { id }),
  /** Alertes dashboard. */
  alertes: (domaineId: string | null) => tenantQueryKey(domaineId, "parcMateriel.listerAlertes"),
};

function useContexteLectureMateriel() {
  const { data: me } = useMe();
  const { granted: canRead } = useCan("materiel.read");
  return { domaineId: resoudreDomaineIdActif(me), canRead };
}

// ────────────────────────────────────────────────────────────────────────────
// Hooks de lecture
// ────────────────────────────────────────────────────────────────────────────

/** Liste du parc, filtrée par statut (défaut : actifs seuls côté back). */
export function useParcMateriel(statut: "actif" | "reforme" | undefined) {
  const { domaineId, canRead } = useContexteLectureMateriel();
  return useQuery({
    queryKey: materielQueryKeys.parc(domaineId, statut),
    queryFn: () => trpc.parcMateriel.listerParc.query(statut ? { statut } : undefined),
    enabled: Boolean(domaineId && canRead),
  });
}

/** Fiche détail d'un matériel (matériel + plans + historique). */
export function useMaterielDetail(id: string | undefined) {
  const { domaineId, canRead } = useContexteLectureMateriel();
  return useQuery({
    queryKey: materielQueryKeys.fiche(domaineId, id ?? ""),
    queryFn: () => trpc.parcMateriel.getMateriel.query({ id: id! }),
    enabled: Boolean(domaineId && canRead && id),
  });
}

/** Alertes matériel du tenant (consommé par le dashboard). */
export function useAlertesMateriel() {
  const { domaineId, canRead } = useContexteLectureMateriel();
  return useQuery({
    queryKey: materielQueryKeys.alertes(domaineId),
    queryFn: () => trpc.parcMateriel.listerAlertes.query(),
    enabled: Boolean(domaineId && canRead),
  });
}
